import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Get theme from localStorage or default to 'light'
    const savedTheme = localStorage.getItem('faithinsight-theme');
    return savedTheme || 'light';
  });

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('faithinsight-theme', newTheme);
  };

  useEffect(() => {
    // Apply theme to document root
    const root = document.documentElement;
    const body = document.body;
    
    // Add/remove dark mode class for CSS selectors
    if (theme === 'dark') {
      body.classList.add('dark-mode');
      body.classList.remove('light-mode');
    } else {
      body.classList.add('light-mode');
      body.classList.remove('dark-mode');
    }
    
    if (theme === 'dark') {
      // Dark theme colors - Warm charcoal theme (not blue-gray)
      
      // Border Radius Tokens (same for both themes)
      root.style.setProperty('--border-radius', '12px');
      root.style.setProperty('--border-radius-lg', '18px');
      root.style.setProperty('--border-radius-xl', '22px');
      root.style.setProperty('--card-radius', '18px');
      root.style.setProperty('--control-radius', '12px');
      root.style.setProperty('--pill-radius', '8px');
      
      // Surface Colors - Warm dark surfaces
      root.style.setProperty('--surface-color', '#1F1C18');
      root.style.setProperty('--surface-tint', '#2A2520');
      
      // Primary Brand Colors (slightly adjusted for dark mode)
      root.style.setProperty('--primary-color', '#D8B889');
      root.style.setProperty('--primary-hover', '#E5C9A3');
      root.style.setProperty('--primary-light', '#F0D9B8');
      root.style.setProperty('--primary-dark', '#C4A574');
      
      // Secondary & Accent Colors
      root.style.setProperty('--secondary-color', '#C6AB82');
      root.style.setProperty('--accent-color', '#A68B6B');
      root.style.setProperty('--accent-light', '#B89A7A');
      
      // Modern Contrast Colors (adjusted for dark backgrounds)
      root.style.setProperty('--contrast-primary', '#E2E8F0');
      root.style.setProperty('--contrast-secondary', '#CBD5E0');
      root.style.setProperty('--contrast-accent', '#90CDF4');
      root.style.setProperty('--contrast-warm', '#F6AD55');
      root.style.setProperty('--contrast-cool', '#63B3ED');
      
      // Status Colors (lighter variants for dark mode)
      root.style.setProperty('--success-color', '#68D391');
      root.style.setProperty('--success-light', '#9AE6B4');
      root.style.setProperty('--success-dark', '#48BB78');
      root.style.setProperty('--warning-color', '#F6E05E');
      root.style.setProperty('--warning-light', '#FAF089');
      root.style.setProperty('--warning-dark', '#ECC94B');
      root.style.setProperty('--error-color', '#FC8181');
      root.style.setProperty('--error-light', '#FEB2B2');
      root.style.setProperty('--error-dark', '#F56565');
      root.style.setProperty('--info-color', '#63B3ED');
      root.style.setProperty('--info-light', '#90CDF4');
      root.style.setProperty('--info-dark', '#4299E1');
      
      // Text Colors (light text on dark backgrounds)
      root.style.setProperty('--text-primary', '#F7FAFC');
      root.style.setProperty('--text-secondary', '#E2E8F0');
      root.style.setProperty('--text-muted', '#A0AEC0');
      root.style.setProperty('--text-light', '#718096');
      root.style.setProperty('--text-inverse', '#1A202C');
      
      // Background Colors - Warm charcoals (not blue-gray)
      root.style.setProperty('--background-primary', '#1F1C18');
      root.style.setProperty('--background-secondary', '#14110E');
      root.style.setProperty('--background-tertiary', '#2A2520');
      root.style.setProperty('--background-quaternary', '#352F28');
      root.style.setProperty('--background-dark', '#0F0D0A');
      root.style.setProperty('--background-overlay', 'rgba(0, 0, 0, 0.7)');
      
      // Border Colors - Warm translucent borders
      root.style.setProperty('--border-color', 'rgba(255, 255, 255, 0.10)');
      root.style.setProperty('--border-light', 'rgba(255, 255, 255, 0.15)');
      root.style.setProperty('--border-dark', 'rgba(255, 255, 255, 0.08)');
      
      // Modern Shadows - Enhanced for dark mode
      root.style.setProperty('--shadow-sm', '0 2px 8px rgba(0, 0, 0, 0.3)');
      root.style.setProperty('--shadow-md', '0 10px 26px rgba(0, 0, 0, 0.4), 0 2px 10px rgba(0, 0, 0, 0.2)');
      root.style.setProperty('--shadow-lg', '0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.3)');
      root.style.setProperty('--shadow-xl', '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.3)');
      root.style.setProperty('--shadow-float', '0 18px 45px rgba(0, 0, 0, 0.5), 0 2px 10px rgba(0, 0, 0, 0.3)');
      root.style.setProperty('--shadow-colored', '0 4px 14px 0 rgba(216, 184, 137, 0.4)');
      
      // Interactive States
      root.style.setProperty('--hover-overlay', 'rgba(216, 184, 137, 0.15)');
      root.style.setProperty('--focus-ring', 'rgba(216, 184, 137, 0.5)');
      root.style.setProperty('--active-overlay', 'rgba(216, 184, 137, 0.25)');
    } else {
      // Light theme colors (default)
      
      // Border Radius Tokens (same for both themes)
      root.style.setProperty('--border-radius', '12px');
      root.style.setProperty('--border-radius-lg', '18px');
      root.style.setProperty('--border-radius-xl', '22px');
      root.style.setProperty('--card-radius', '18px');
      root.style.setProperty('--control-radius', '12px');
      root.style.setProperty('--pill-radius', '8px');
      
      // Surface Colors - Warm light surfaces
      root.style.setProperty('--surface-color', '#ffffff');
      root.style.setProperty('--surface-tint', '#fbf8f3');
      
      // Darkened primary color for better contrast in light mode
      root.style.setProperty('--primary-color', '#B8956A');
      root.style.setProperty('--primary-hover', '#A6855A');
      root.style.setProperty('--primary-light', '#C4A574');
      root.style.setProperty('--primary-dark', '#9A7A4F');
      root.style.setProperty('--secondary-color', '#C6AB82');
      root.style.setProperty('--accent-color', '#877965');
      root.style.setProperty('--accent-light', '#A68B6B');
      root.style.setProperty('--contrast-primary', '#2D3748');
      root.style.setProperty('--contrast-secondary', '#4A5568');
      root.style.setProperty('--contrast-accent', '#1A365D');
      root.style.setProperty('--contrast-warm', '#C05621');
      root.style.setProperty('--contrast-cool', '#2B6CB0');
      root.style.setProperty('--success-color', '#38A169');
      root.style.setProperty('--success-light', '#68D391');
      root.style.setProperty('--success-dark', '#2F855A');
      root.style.setProperty('--warning-color', '#D69E2E');
      root.style.setProperty('--warning-light', '#F6E05E');
      root.style.setProperty('--warning-dark', '#B7791F');
      root.style.setProperty('--error-color', '#E53E3E');
      root.style.setProperty('--error-light', '#FC8181');
      root.style.setProperty('--error-dark', '#C53030');
      root.style.setProperty('--info-color', '#3182CE');
      root.style.setProperty('--info-light', '#63B3ED');
      root.style.setProperty('--info-dark', '#2C5282');
      root.style.setProperty('--text-primary', '#1A202C');
      root.style.setProperty('--text-secondary', '#2D3748');
      root.style.setProperty('--text-muted', '#4A5568');
      root.style.setProperty('--text-light', '#718096');
      root.style.setProperty('--text-inverse', '#FFFFFF');
      root.style.setProperty('--background-primary', '#FFFFFF');
      root.style.setProperty('--background-secondary', '#F7F5F1');
      root.style.setProperty('--background-tertiary', '#F0EDE7');
      root.style.setProperty('--background-quaternary', '#E8E5DF');
      root.style.setProperty('--background-dark', '#2D3748');
      root.style.setProperty('--background-overlay', 'rgba(45, 55, 72, 0.8)');
      
      // Border Colors - Warm borders (matches index.css canonical)
      root.style.setProperty('--border-color', 'rgba(230, 225, 218, 0.95)');
      root.style.setProperty('--border-light', '#E5E7EB');
      root.style.setProperty('--border-dark', '#9CA3AF');
      
      // Modern Shadows - Enhanced for light mode
      root.style.setProperty('--shadow-sm', '0 2px 8px rgba(17, 24, 39, 0.06)');
      root.style.setProperty('--shadow-md', '0 10px 26px rgba(17, 24, 39, 0.10), 0 2px 10px rgba(17, 24, 39, 0.06)');
      root.style.setProperty('--shadow-lg', '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)');
      root.style.setProperty('--shadow-xl', '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)');
      root.style.setProperty('--shadow-float', '0 18px 45px rgba(17, 24, 39, 0.12), 0 2px 10px rgba(17, 24, 39, 0.06)');
      root.style.setProperty('--shadow-colored', '0 4px 14px 0 rgba(216, 184, 137, 0.25)');
      
      // Interactive States
      root.style.setProperty('--hover-overlay', 'rgba(216, 184, 137, 0.1)');
      root.style.setProperty('--focus-ring', 'rgba(176, 141, 87, 0.45)');
      root.style.setProperty('--active-overlay', 'rgba(216, 184, 137, 0.2)');
    }
  }, [theme]);

  const value = {
    theme,
    toggleTheme,
    isDark: theme === 'dark'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
